import { TransactionContext } from "@/app/contexts/TransactionContext";
import { Transaction } from "@/app/entities/Transaction";
import { handleSupport } from "@/app/services/transactionService/handleSupport";
import { selectedIdfromTableRowTransaction } from "@/app/store";
import { TransactionByIdResponse } from "@/app/services/transactionService/transactionById";
import { Button } from "@/view/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/view/components/ui/form";
import { Input } from "@/view/components/ui/input";
import { Spinner } from "@/view/components/ui/spinner";
import { Switch } from "@/view/components/ui/switch";
import { Textarea } from "@/view/components/ui/textarea";
import { toast } from "@/view/components/ui/use-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAtom } from "jotai";
import { useContext } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const validationSchema = z.object({
  link: z.string().optional(),
  observation: z.string().optional(),
  canUploadDocs: z.boolean().default(false),
});

type FormData = z.infer<typeof validationSchema>;

export const TransactionSupport = ({
  transaction,
}: {
  transaction?: Transaction | null;
}) => {
  const { refetch } = useContext(TransactionContext);
  const [selectedId] = useAtom(selectedIdfromTableRowTransaction);
  const queryClient = useQueryClient();

  const transactionId = transaction?._id ?? selectedId;
  const cachedTransaction = queryClient.getQueryData<TransactionByIdResponse>([
    "transactions",
    "transactionById",
    transactionId,
  ]);

  const support = transaction?.support ?? cachedTransaction?.support;

  const form = useForm<FormData>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      link: support?.link,
      observation: support?.observation,
      canUploadDocs: support?.canUploadDocs,
    },
  });

  const { handleSubmit } = form;

  const { mutateAsync: saveSupport, isPending } = useMutation({
    mutationFn: async (data: FormData) =>
      transactionId &&
      (await handleSupport({
        id: transactionId,
        data: { ...data, isStatusLocked: true },
      })),
  });
  const onSubmit = handleSubmit(async (data) => {
    try {
      if (!data.link && !data.canUploadDocs) {
        return toast({
          variant: "destructive",
          title:
            "É necessário informar pelo menos um link ou habilitar o upload de documentos",
        });
      }
      await saveSupport(data);
      refetch();
      toast({
        title: "Suporte salvo com sucesso",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Erro ao salvar suporte",
      });
    }
  });

  return (
    <Form {...form}>
      <form onSubmit={onSubmit}>
        <div className="flex flex-col gap-4">
          <FormField
            control={form.control}
            name="link"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Link</FormLabel>
                <FormControl>
                  <Input placeholder="Link de formalização" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            defaultValue=""
            control={form.control}
            name="observation"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Observação</FormLabel>
                <FormControl>
                  <Textarea placeholder="Observação" {...field} />
                </FormControl>
                <FormMessage />
                <div className="flex justify-end">
                  <span className="text-sm text-muted-foreground">
                    {field.value?.length}/500
                  </span>
                </div>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="canUploadDocs"
            render={({ field }: any) => (
              <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <FormLabel className="text-base">Enviar documentos</FormLabel>
                </div>
                <FormControl>
                  <Switch
                    checked={Boolean(field.value)}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <Button disabled={isPending} className="text-white" type="submit">
            {isPending ? <Spinner className="text-white" /> : "Salvar"}
          </Button>
        </div>
      </form>
    </Form>
  );
};
